// Lazy boundary for Tiptap (~150 KB gzip). Plain-Preact dynamic
// `import()` rather than `preact/compat`'s `lazy`/`Suspense`: importing
// `preact/compat` patches Preact's render pipeline globally (synthetic
// events, batched setState) and broke unrelated form tests relying on
// plain-Preact microtask flushing. The dynamic `import()` is what
// drives Vite's code-split; the cache pattern is what avoids a
// skeleton flash on the second mount.

import { useEffect, useState } from 'preact/hooks'

import { createLazyComponentLoader } from './lazy-component'
import type { RichTextField as RichTextFieldComponent } from './richtext-field'

type RichTextFieldType = typeof RichTextFieldComponent

const richTextLoader = createLazyComponentLoader(
  () => import('./richtext-field'),
  (m) => m.RichTextField,
)

export interface LazyRichTextFieldProps {
  readonly id: string
  readonly name: string
  readonly label: string
  readonly helpText?: string
  readonly value: string
  readonly onChange: (next: string) => void
  readonly onBlur?: () => void
  readonly required?: boolean
  readonly error?: string
}

interface SkeletonProps {
  readonly id: string
  readonly label: string
  readonly helpText?: string
  readonly required: boolean
}

function RichTextSkeleton({ id, label, helpText, required }: SkeletonProps) {
  return (
    <div class="flex flex-col gap-1" aria-busy="true">
      <label for={id} class="text-sm font-medium text-gray-800">
        {label}
        {required && <span class="text-red-700"> *</span>}
      </label>
      {helpText !== undefined && <span class="text-xs text-gray-500">{helpText}</span>}
      <div
        id={id}
        role="presentation"
        class="min-h-[8rem] rounded border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-400"
      >
        Loading editor…
      </div>
    </div>
  )
}

export function LazyRichTextField(props: LazyRichTextFieldProps) {
  // Thunks needed because Preact treats function-typed init/update args
  // as lazy initializers / updaters; storing the component value
  // directly would call it.
  const [Component, setComponent] = useState<RichTextFieldType | null>(() => richTextLoader.get())

  useEffect(() => {
    if (Component !== null) return
    let cancelled = false
    richTextLoader.load().then((C) => {
      if (!cancelled) setComponent(() => C)
    })
    return () => {
      cancelled = true
    }
  }, [Component])

  if (Component === null) {
    return (
      <RichTextSkeleton
        id={props.id}
        label={props.label}
        required={props.required ?? false}
        {...(props.helpText !== undefined ? { helpText: props.helpText } : {})}
      />
    )
  }
  return <Component {...props} />
}

export const resetLazyRichTextFieldForTest = richTextLoader.resetForTest
